/**
 * PAJ - Creating a Global Shared State using Context API
 * state defined in this AppContextProvider will be used throughout the context of our application
 */
import React, { useEffect, useState } from 'react';
import { IAppProps, REDUCER_KEY } from '../interface/IAppProvider';
import { appContextReducer } from '../contextReducer/AppContextReducer';
import { AppStateContext, AppDispatchContext } from './AppContext';
import initialState from '../initialState/initialState';
import { removeSessionItem } from '../services/StorageService';
import { ROUTE_NAMES } from '../../router/RouterApp';
import { reloadApplicationStateFromCache } from '../actions/ObjectCacheActions';
import { track } from 'c9-tracking';
import { getFullPathName } from '../utils/helpers/GenericHelpers';
import { useLocation, useNavigate } from 'react-router-dom';

export const AppContextProvider: React.FunctionComponent<IAppProps> = (props) => {
    const [state, dispatch] = React.useReducer(appContextReducer, initialState);
    const [refresh, setRefresh] = React.useState(false);
    const [trackReady, setTrackReady] = React.useState(false);
    const [backButtonClicked, setBackButtonClicked] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const isReceipt = props.config.baseName.indexOf('kvittering') !== -1;

    //Browser back and forward
    window.onpopstate = (event: PopStateEvent) => {
        setBackButtonClicked(true);
        //push state to handle browser back forward
        if (typeof event.state == 'object' && event.state && !event.state.obsolete) {
            let newLocation = { ...location, state: { obsolete: true } };
            navigate(newLocation, { replace: true });
            //push to history so forward button get disabled
            navigate(newLocation);
        }
        if (typeof event.state == 'object' && event.state && event.state.state && event.state.state.obsolete) {
            //we are pushing two steps to history so need go back -2 to go back in the flow
            navigate(-2);
        }
    };

    useEffect(() => {
        const pathName = window.location.pathname;
        if (!trackReady) {
            setTrackReady(true);
            track.ready(true); //Initialize Track library for sas360 tracking
        }

        //Handle refresh
        if (!state.refreshFromCache && !refresh) {
            setRefresh(true);
            //Check user route to / or intro page
            if (
                !isReceipt &&
                (pathName === getFullPathName(props.config.baseName, ROUTE_NAMES.CVR, false) ||
                    pathName === props.config.baseName ||
                    pathName === getFullPathName(props.config.baseName, ROUTE_NAMES.CVR, true))
            ) {
                removeSessionItem('onsa-b2b-session-key');
            }
            reloadApplicationStateFromCache(props, dispatch);
        }

        //back button with filled data
        if (
            backButtonClicked &&
            (pathName === getFullPathName(props.config.baseName, ROUTE_NAMES.CVR, false) ||
                pathName === props.config.baseName ||
                pathName === getFullPathName(props.config.baseName, ROUTE_NAMES.CVR, true))
        ) {
            setBackButtonClicked(false);
            removeSessionItem('onsa-b2b-session-key');
            dispatch({
                type: REDUCER_KEY.RESET_FROM_BACK_BUTTON,
                payload: {
                    cvrData: state.cvrData,
                },
            });
            dispatch({ type: REDUCER_KEY.REFRESH_FROM_CACHE, payload: true });
        } else {
            setBackButtonClicked(false);
        }
    }, [
        props,
        refresh,
        dispatch,
        state.refreshFromCache,
        state.emailSent,
        state.salesComplete,
        backButtonClicked,
        state.initialState,
        trackReady,
        state.prevLocation,
        state.cvrData,
        isReceipt,
    ]);

    return (
        <AppStateContext.Provider value={state}>
            <AppDispatchContext.Provider value={dispatch}>{props.children}</AppDispatchContext.Provider>
        </AppStateContext.Provider>
    );
};
